import { useGetList, useTranslate } from 'ra-core';
import { AutocompleteArrayInput } from '../../components/admin/autocomplete-array-input';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { MoonstarListActions } from '../Moonstar/MoonstarListActions';
import { BadgesAwardsChart } from './BadgesAwardsChart';
import { ResourcesValue } from '../resources';
import type { Badge } from './types';
import { BadgeOptionRenderer } from './BadgeOptionRenderer';
import { CenteredSpinner } from '../../components/custom/CenteredSpinner';
import { BadgesAwardsList } from './BadgesAwardsList';
import { createListExporter } from './createListExporter';
import { ListExportButton } from '../../components/custom/ListExportButton';
import { DateRangeInput } from '../../components/custom/DateRangeInput';

export const BadgesAnalytics = () => {
  const { data, isPending } = useGetList(ResourcesValue.BADGES);
  const badgesList = data as Badge[];
  const translate = useTranslate();

  // Compute default filters
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const createdAfterDefault = firstDayOfMonth.toISOString().split('T')[0]!;
  const createdBeforeDefault = lastDayOfMonth.toISOString().split('T')[0]!;

  if (isPending) {
    return <CenteredSpinner />;
  }

  const BasicBadgeAwardsFilters = [
    <AutocompleteArrayInput
      key="badges"
      source="badgesId"
      choices={badgesList.sort((a, b) => a.id - b.id)}
      optionText={(record: Badge) => <BadgeOptionRenderer record={record} />}
      inputText={(record: Badge) => record.name}
      alwaysOn
    />,
    <DateRangeInput
      key="awarded-on"
      startSource="createdAfter"
      endSource="createdBefore"
      label="resources.badgeAwards.fields.awardedOn"
      alwaysOn
    />,
  ];

  const chartTitle = translate(
    badgesList.length
      ? 'moonstar.badges.analytics.selectedBadgesAwards'
      : 'moonstar.badges.analytics.allBadgesAwards'
  );

  return (
    <MoonstarList
      resource={ResourcesValue.BADGES_AWARDS}
      filters={BasicBadgeAwardsFilters}
      filterDefaultValues={{
        badgesId: badgesList.flatMap((b) => b.id),
        createdAfter: createdAfterDefault,
        createdBefore: createdBeforeDefault,
      }}
      sort={{ field: 'awardedOn', order: 'DESC' }}
      actions={
        <MoonstarListActions
          hideCreate
          right={<ListExportButton maxResults={Infinity} />}
        />
      }
      exporter={createListExporter(ResourcesValue.BADGES_AWARDS)}
      debounce={1000}
    >
      <div className="flex flex-col gap-4">
        <BadgesAwardsChart title={chartTitle} name={chartTitle} />

        <BadgesAwardsList />
      </div>
    </MoonstarList>
  );
};
